import { useCallback, useEffect, useRef, useState } from 'react';

type PollingState<T> = {
  readonly data: T | undefined;
  readonly error: unknown;
  readonly loading: boolean;
  readonly reload: () => Promise<void>;
};

/** Online-only data (not in the replica): load now, then every `intervalMs` while the page is visible. */
export function usePolling<T>(load: () => Promise<T>, intervalMs: number): PollingState<T> {
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>();
  const [loading, setLoading] = useState(true);
  const loadRef = useRef(load);
  loadRef.current = load;

  const reload = useCallback(async function reloadNow() {
    try {
      setData(await loadRef.current());
      setError(undefined);
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(
    function poll() {
      void reload();
      const timer = window.setInterval(() => {
        if (document.visibilityState === 'visible') void reload();
      }, intervalMs);
      return () => window.clearInterval(timer);
    },
    [reload, intervalMs],
  );

  return { data, error, loading, reload };
}
